import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import EasternGalileeMapPicker from '@/components/map/EasternGalileeMapPicker';
import MapLegend from '@/components/map/MapLegend';
import JourneyProgressStepper from '@/components/wizard/JourneyProgressStepper';
import { AppBanner, Card, JourneyScreenBack } from '@/components/ui';
import PageShell from '@/components/ui/PageShell';
import { MapDesignationFilterProvider, useMapDesignationFilter } from '@/context/MapDesignationFilterContext';
import {
  clearIntakeSubmitted,
  clearWizardConsentDone,
  useWizardSession,
} from '@/context/WizardSessionContext';
import { useTranslation } from '@/i18n/useTranslation';
import type { ParcelIdentifyResult } from '@/lib/api/map';
import { answersToApi } from '@/lib/api/session-mapper';
import { getSessionParcel, patchSessionSteps, postSessionParcel } from '@/lib/api/sessions';
import { JOURNEY_STEP, JOURNEY_UI_STEP } from '@/lib/journey-steps';
import { loadMapLegend, prefetchMapConfig } from '@/lib/map/map-cache';
import { clearAssessmentPlanningCache } from '@/lib/assessment-planning-cache';
import { isValidMapPoint } from '@/lib/validation/parcel';
import styles from './MapScreen.module.css';

type MapLocationState = { revisiting?: boolean };

function MapScreenContent() {
  const t = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const revisiting = Boolean((location.state as MapLocationState | null)?.revisiting);
  const { sessionId, answers } = useWizardSession();
  const { enabledCodes, entries } = useMapDesignationFilter();

  const enabledDesignationCodes = useMemo(
    () => [...enabledCodes],
    [enabledCodes],
  );
  const allDesignationCodes = useMemo(
    () => entries.map((e) => e.code).filter(Boolean),
    [entries],
  );

  const [mapLat, setMapLat] = useState<number | undefined>();
  const [mapLng, setMapLng] = useState<number | undefined>();
  const [parcelGeometry, setParcelGeometry] = useState<{
    type: string;
    coordinates: unknown;
  } | null>(null);
  const [selectionMode, setSelectionMode] = useState<'point' | 'polygon_drawn'>('point');
  const [pendingParcel, setPendingParcel] = useState<ParcelIdentifyResult | null>(null);
  const [identifyError, setIdentifyError] = useState<string | undefined>();
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    prefetchMapConfig();
    void loadMapLegend().catch(() => {
      /* MapLegend surfaces load errors */
    });
  }, []);

  useEffect(() => {
    if (!sessionId || revisiting) return;
    void patchSessionSteps(sessionId, {
      current_step: JOURNEY_STEP.MAP,
      answers: answersToApi(answers),
    }).catch(() => {
      /* best-effort journey sync */
    });
    void getSessionParcel(sessionId)
      .then(() => {
        navigate('/questionnaire', { replace: true });
      })
      .catch(() => {
        /* no saved parcel yet — stay on map */
      });
  }, [sessionId, answers, navigate, revisiting]);

  const handleMapParcelIdentified = useCallback((result: ParcelIdentifyResult) => {
    setIdentifyError(undefined);
    setConfirmError(null);
    setParcelGeometry(result.geometry ?? null);
    setSelectionMode(result.selection_mode ?? 'point');
    setMapLat(result.lat);
    setMapLng(result.lng);
    setPendingParcel(result);
  }, []);

  const canConfirm =
    pendingParcel != null &&
    Boolean(pendingParcel.designation?.trim()) &&
    Boolean(pendingParcel.migrash?.trim()) &&
    isValidMapPoint(mapLat, mapLng);

  const mapSelectedLabel = useMemo(() => {
    if (!pendingParcel) return undefined;
    const parts: string[] = [];
    if (pendingParcel.designation) {
      parts.push(pendingParcel.designation);
    }
    if (pendingParcel.area_sqm_drawn != null) {
      parts.push(`${t.map.drawnAreaLabel}: ~${pendingParcel.area_sqm_drawn.toLocaleString('he-IL')} מ"ר`);
    } else if (pendingParcel.settlement_name) {
      parts.push(pendingParcel.settlement_name);
    }
    if (mapLat != null && mapLng != null && pendingParcel.selection_mode !== 'polygon_drawn') {
      parts.push(`${mapLat.toFixed(5)}, ${mapLng.toFixed(5)}`);
    }
    return parts.length > 0 ? parts.join(' · ') : undefined;
  }, [pendingParcel, mapLat, mapLng, t.map.drawnAreaLabel]);

  const handleConfirm = async () => {
    if (!sessionId || !pendingParcel || !canConfirm || !pendingParcel.migrash) return;
    setLoading(true);
    setConfirmError(null);
    try {
      clearAssessmentPlanningCache(sessionId);
      clearWizardConsentDone();
      clearIntakeSubmitted();
      await postSessionParcel(sessionId, {
        migrash: pendingParcel.migrash,
        lat: pendingParcel.lat,
        lng: pendingParcel.lng,
        settlement_name: pendingParcel.settlement_name,
        designation: pendingParcel.designation,
        designation_code: pendingParcel.designation_code,
        plan_primary: pendingParcel.plan_primary,
        plans_affecting: pendingParcel.plans_affecting,
        area_sqm_registered: pendingParcel.area_sqm_registered,
        geometry: pendingParcel.geometry,
        selection_mode: pendingParcel.selection_mode ?? selectionMode,
      });
      await patchSessionSteps(sessionId, {
        current_step: JOURNEY_STEP.INTAKE_VENTURE,
        answers: answersToApi(answers),
      }).catch(() => {
        /* best-effort */
      });
      navigate('/questionnaire', { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : t.map.errorTitle;
      setConfirmError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell
      width="map"
      header={<JourneyScreenBack />}
      progress={<JourneyProgressStepper activeIndex={JOURNEY_UI_STEP.MAP} />}
    >
      <Card className={styles.screen} data-testid="map-screen">
        <header className={styles.header}>
          <h1 className={styles.title}>{t.map.title}</h1>
          <p className={styles.subtitle}>{t.map.subtitle}</p>
        </header>

        <div className={styles.mapUnit}>
          <div className={styles.mapStage}>
            <EasternGalileeMapPicker
              embedded
              lat={mapLat}
              lng={mapLng}
              parcelGeometry={parcelGeometry}
              selectionMode={selectionMode}
              onSelectionModeChange={setSelectionMode}
              onParcelIdentified={handleMapParcelIdentified}
              onIdentifyError={(message) =>
                setIdentifyError(
                  message === 'IDENTIFY_TIMEOUT'
                    ? t.map.validation.identifyTimeout
                    : message || t.map.validation.identifyFailed,
                )
              }
              selectedLabel={mapSelectedLabel}
              identifyError={identifyError}
              mapAriaLabel={t.map.mapAriaLabel}
              enabledDesignationCodes={enabledDesignationCodes}
              allDesignationCodes={allDesignationCodes}
            />
            <MapLegend variant="overlay" />
          </div>
        </div>

        {pendingParcel ? (
          <div className={styles.confirmCard} data-testid="parcel-confirm-card">
            <div className={styles.confirmPanel}>
              <div className={styles.confirmContent}>
                <h2 className={styles.confirmTitle}>{t.map.confirmTitle}</h2>
                <p className={styles.confirmLead}>{t.map.confirmBody}</p>
                <dl className={styles.confirmDetails}>
                  {pendingParcel.designation ? (
                    <div className={styles.confirmRow}>
                      <dt>{t.map.fields.designation}</dt>
                      <dd>{pendingParcel.designation}</dd>
                    </div>
                  ) : null}
                  {pendingParcel.area_sqm_drawn != null ? (
                    <div className={styles.confirmRow}>
                      <dt>{t.map.drawnAreaLabel}</dt>
                      <dd>~{pendingParcel.area_sqm_drawn.toLocaleString('he-IL')} מ"ר</dd>
                    </div>
                  ) : null}
                  {pendingParcel.settlement_name ? (
                    <div className={styles.confirmRow}>
                      <dt>{t.map.fields.settlement}</dt>
                      <dd>{pendingParcel.settlement_name}</dd>
                    </div>
                  ) : null}
                  {mapLat != null && mapLng != null ? (
                    <div className={styles.confirmRow}>
                      <dt>{t.map.fields.coordinates}</dt>
                      <dd dir="ltr">
                        {mapLat.toFixed(5)}, {mapLng.toFixed(5)}
                      </dd>
                    </div>
                  ) : null}
                </dl>
                {confirmError ? (
                  <AppBanner variant="danger" title={t.map.errorTitle} text={confirmError} compact />
                ) : null}
              </div>
              <button
                type="button"
                className={styles.confirmButton}
                onClick={() => void handleConfirm()}
                disabled={loading || !canConfirm}
                aria-busy={loading}
                data-testid="parcel-confirm-button"
              >
                {loading ? t.map.confirmButtonLoading : t.map.confirmButton}
              </button>
            </div>
          </div>
        ) : null}
      </Card>
    </PageShell>
  );
}

export default function MapScreen() {
  return (
    <MapDesignationFilterProvider>
      <MapScreenContent />
    </MapDesignationFilterProvider>
  );
}
